import React, { useState } from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { HelpCircle } from 'lucide-react';

const FAQ = ({ onOpenConsultation }) => {
  const { faqItems } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  // Comprehensive FAQ translations for all pages
  const faqTranslations = {
    // Main page FAQs
    'Wie lange dauert eine Haartransplantation?': {
      en: 'How long does a hair transplant take?'
    },
    'Je nach Anzahl der zu transplantierenden Grafts dauert der Eingriff zwischen 4 und 8 Stunden. Sie können während der Behandlung Musik hören, Filme schauen oder sich entspannen.': {
      en: 'Depending on the number of grafts to be transplanted, the procedure takes between 4 and 8 hours. You can listen to music, watch movies or relax during the treatment.'
    },
    'Ist die Behandlung schmerzhaft?': {
      en: 'Is the treatment painful?'
    },
    'Nein, die Behandlung erfolgt unter lokaler Betäubung und ist praktisch schmerzfrei. Nur die initiale Betäubung kann als leichtes Piksen empfunden werden.': {
      en: 'No, the treatment is performed under local anesthesia and is virtually painless. Only the initial anesthesia may be felt as a slight prick.'
    },
    'Wann sehe ich die ersten Ergebnisse?': {
      en: 'When will I see the first results?'
    },
    'Die transplantierten Haare fallen zunächst aus (Shock Loss), was normal ist. Nach 3-4 Monaten beginnt das neue Wachstum, das volle Ergebnis sehen Sie nach 12-18 Monaten.': {
      en: 'The transplanted hair initially falls out (Shock Loss), which is normal. After 3-4 months the new growth begins, you will see the full result after 12-18 months.'
    },
    'Wie hoch ist die Anwachsrate?': {
      en: 'What is the growth rate?'
    },
    'Bei unseren Behandlungen liegt die Anwachsrate bei über 95%. Durch unsere langjährige Erfahrung und modernste Techniken erzielen wir hervorragende Ergebnisse.': {
      en: 'Our treatments have a growth rate of over 95%. Thanks to our years of experience and state-of-the-art techniques, we achieve excellent results.'
    },
    'An welchem Standort kann ich mich behandeln lassen?': {
      en: 'At which location can I receive treatment?'
    },
    'Unsere Klinik befindet sich in Mönchengladbach und ist ideal erreichbar aus Köln, Düsseldorf, dem Ruhrgebiet und den Niederlanden.': {
      en: 'Our clinic is located in Mönchengladbach and is easily accessible from Cologne, Düsseldorf, the Ruhr area and the Netherlands.'
    },
    'Sind die Ergebnisse dauerhaft?': {
      en: 'Are the results permanent?'
    },
    'Ja! Die transplantierten Haare stammen aus dem Spenderbereich am Hinterkopf, der genetisch resistent gegen Haarausfall ist. Diese Haare wachsen ein Leben lang.': {
      en: 'Yes! The transplanted hair comes from the donor area at the back of the head, which is genetically resistant to hair loss. This hair grows for a lifetime.'
    },
    
    // Eyebrow page FAQs
    'Wie unterscheidet sich eine Augenbrauentransplantation von Microblading?': {
      en: 'How does an eyebrow transplant differ from microblading?'
    },
    'Bei der Transplantation wachsen echte Haare, die dauerhaft bleiben und normal gestylt werden können. Microblading ist Pigmentierung, die alle 1-2 Jahre aufgefrischt werden muss.': {
      en: 'With the transplant, real hair grows that remains permanent and can be styled normally. Microblading is pigmentation that needs to be refreshed every 1-2 years.'
    },
    'Wie lange dauert der Eingriff?': {
      en: 'How long does the procedure take?'
    },
    'Eine Augenbrauentransplantation dauert je nach Umfang 2-4 Stunden. Die Zeit verfliegt schnell - Sie können Musik hören oder einen Film schauen.': {
      en: 'An eyebrow transplant takes 2-4 hours depending on the scope. Time flies - you can listen to music or watch a movie.'
    },
    'Muss ich die transplantierten Haare schneiden?': {
      en: 'Do I need to trim the transplanted hair?'
    },
    'Ja, da die Haare vom Kopf stammen, wachsen sie etwas schneller als normale Augenbrauenhaare. Ein gelegentliches Trimmen ist erforderlich.': {
      en: 'Yes, since the hair comes from the head, it grows slightly faster than normal eyebrow hair. Occasional trimming is required.'
    },
    'Ist der Eingriff schmerzhaft?': {
      en: 'Is the procedure painful?'
    },
    'Nein, die Behandlung erfolgt unter lokaler Betäubung. Sie spüren lediglich ein leichtes Piksen bei der Betäubung.': {
      en: 'No, the treatment is performed under local anesthesia. You will only feel a slight prick during the anesthesia.'
    },
    'Wann sehe ich das endgültige Ergebnis?': {
      en: 'When will I see the final result?'
    },
    'Die transplantierten Haare fallen zunächst aus (Shock Loss), wachsen aber nach 3-4 Monaten nach. Das volle Ergebnis sehen Sie nach 9-12 Monaten.': {
      en: 'The transplanted hair initially falls out (Shock Loss), but grows back after 3-4 months. You will see the full result after 9-12 months.'
    },
    'Kann ich nach der Transplantation Make-up tragen?': {
      en: 'Can I wear makeup after the transplant?'
    },
    'In den ersten 10-14 Tagen sollten Sie die Augenbrauenregion nicht schminken. Danach können Sie wie gewohnt Make-up verwenden.': {
      en: 'You should not apply makeup to the eyebrow area for the first 10-14 days. After that, you can use makeup as usual.'
    },
    'Wo befindet sich Ihre Klinik?': {
      en: 'Where is your clinic located?'
    },
    
    // Beard page FAQs
    'Wie viele Grafts benötige ich für einen Vollbart?': {
      en: 'How many grafts do I need for a full beard?'
    },
    'Die Anzahl variiert je nach gewünschtem Ergebnis. Für einen Vollbart werden typischerweise 2.500-4.000 Grafts benötigt, bei Lücken oder Konturierung deutlich weniger.': {
      en: 'The number varies depending on the desired result. A full beard typically requires 2,500-4,000 grafts, significantly fewer for filling gaps or contouring.'
    },
    'Wie lange dauert eine Barttransplantation?': {
      en: 'How long does a beard transplant take?'
    },
    'Je nach Umfang dauert der Eingriff 3-6 Stunden. Sie können währenddessen Musik hören oder einen Film schauen.': {
      en: 'Depending on the scope, the procedure takes 3-6 hours. You can listen to music or watch a movie during the treatment.'
    },
    'Kann ich danach rasieren?': {
      en: 'Can I shave afterward?'
    },
    'Ja, nach etwa 3-4 Wochen können Sie sich wieder normal rasieren und den Bart stylen wie Sie möchten.': {
      en: 'Yes, after about 3-4 weeks you can shave normally again and style your beard as you wish.'
    },
    'Sieht das Ergebnis natürlich aus?': {
      en: 'Does the result look natural?'
    },
    'Durch die präzise Einzelhaarimplantation im korrekten Winkel ist das Ergebnis nicht von natürlichem Bartwuchs zu unterscheiden.': {
      en: 'Due to precise single hair implantation at the correct angle, the result is indistinguishable from natural beard growth.'
    },
    'Können auch Narben bedeckt werden?': {
      en: 'Can scars also be covered?'
    },
    'Ja, Narben im Bartbereich (z.B. von Verletzungen oder Operationen) können gezielt mit Haartransplantation bedeckt werden.': {
      en: 'Yes, scars in the beard area (e.g. from injuries or operations) can be specifically covered with hair transplantation.'
    },
    'Fallen die transplantierten Haare aus?': {
      en: 'Do the transplanted hairs fall out?'
    },
    'Die transplantierten Haare fallen zunächst aus (Shock Loss), wachsen aber nach 3-4 Monaten dauerhaft nach. Das ist ein normaler Teil des Prozesses.': {
      en: 'The transplanted hair initially falls out (Shock Loss), but grows back permanently after 3-4 months. This is a normal part of the process.'
    },
  };

  const getTranslatedQuestion = (question) => {
    if (language === 'de') return question;
    return faqTranslations[question]?.en || question;
  };

  const getTranslatedAnswer = (answer) => {
    if (language === 'de') return answer;
    return faqTranslations[answer]?.en || answer;
  };
  
  return (
    <section className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0a0a0a]' : 'bg-white'
    }`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/4 left-0 w-[400px] h-[400px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
        }`} />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
          }`}>
            {t('faq.sectionBadge')}
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">FAQ</span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('faq.sectionDescription')}
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqItems.map((item, index) => {
            // Get translation for FAQ items
            const translatedQuestion = getTranslatedQuestion(item.question);
            const translatedAnswer = getTranslatedAnswer(item.answer);
            
            return (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className={`rounded-xl overflow-hidden transition-colors duration-300 px-6 card-lift ${
                  isDark 
                    ? 'bg-white/[0.02] border border-white/5 hover:border-[#7BA7C2]/20'
                    : 'bg-white border border-gray-200 shadow-sm hover:border-[#5A8BA6]/30'
                }`}
              >
                <AccordionTrigger className={`text-left py-6 hover:no-underline ${
                  isDark ? 'text-white hover:text-[#7BA7C2]' : 'text-gray-900 hover:text-[#5A8BA6]'
                }`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/10'
                    }`}>
                      <HelpCircle className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                    </div>
                    <span className="font-medium">{translatedQuestion}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className={`pb-6 pl-12 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {translatedAnswer}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {/* Still have questions */}
        <div className={`mt-12 text-center p-8 rounded-2xl border ${
          isDark 
            ? 'bg-gradient-to-br from-[#7BA7C2]/10 to-transparent border-[#7BA7C2]/20'
            : 'bg-gradient-to-br from-[#5A8BA6]/5 to-transparent border-[#5A8BA6]/20'
        }`}>
          <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('faq.moreQuestions')}
          </h3>
          <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' 
              ? 'Unser Team beantwortet gerne alle Ihre Fragen persönlich.'
              : 'Our team is happy to answer all your questions personally.'}
          </p>
          <button
            onClick={onOpenConsultation}
            className={`px-6 py-3 rounded-lg font-medium transition-colors duration-300 ${
              isDark 
                ? 'bg-[#7BA7C2] text-white hover:bg-[#5A8BA6]'
                : 'bg-[#5A8BA6] text-white hover:bg-[#7BA7C2]'
            }`}
          >
            {t('faq.contactUs')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default FAQ;